import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Icon,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FiUploadCloud } from "react-icons/fi";
import { QRCodeSVG } from "qrcode.react";
import {
  getExecutionByServiceOrderId,
  getExecutionImageUrl,
  getPublicUrl,
  isVideoMedia,
  saveExecution,
  uploadExecutionImages,
} from "./osExecutionService";

export default function ExecuteOsModal({
  isOpen,
  onClose,
  serviceOrder,
  executedBy,
  canEdit = true,
  initialExecution = null,
  onSaved,
}) {
  const toast = useToast();
  const [executionSummary, setExecutionSummary] = useState("");
  const [executionNotes, setExecutionNotes] = useState("");
  const [savedExecution, setSavedExecution] = useState(null);
  const [pendingImages, setPendingImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !serviceOrder?.serviceOrderid) {
      return;
    }

    const loadExecution = async () => {
      setLoading(true);
      setPendingImages([]);
      try {
        const existing = await getExecutionByServiceOrderId(
          serviceOrder.serviceOrderid
        );
        if (existing) {
          setSavedExecution(existing);
          setExecutionSummary(existing.executionSummary || "");
          setExecutionNotes(existing.executionNotes || "");
        } else {
          setSavedExecution(null);
          setExecutionSummary("");
          setExecutionNotes("");
        }
      } catch (error) {
        toast({
          title: "Erro ao carregar execução",
          description:
            error.response?.data?.message ||
            "Não foi possível buscar a execução.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadExecution();
  }, [isOpen, serviceOrder, toast]);

  const publicUrl = useMemo(() => {
    if (!savedExecution?.publicToken) {
      return "";
    }
    return getPublicUrl(savedExecution.publicToken);
  }, [savedExecution]);

  const handleSelectImages = (event) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(
      (file) =>
        file.type.startsWith("image/") || file.type.startsWith("video/")
    );

    if (!validFiles.length) {
      toast({
        title: "Tipo inválido",
        description: "Selecione apenas arquivos de imagem ou vídeo.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newItems = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isVideo: file.type.startsWith("video/"),
    }));

    setPendingImages((prev) => [...prev, ...newItems]);
    event.target.value = "";
  };

  const uploadPendingImages = async (serviceOrderId) => {
    if (!pendingImages.length) {
      return savedExecution;
    }

    const uploaded = await uploadExecutionImages(
      serviceOrderId,
      pendingImages.map((item) => item.file)
    );

    pendingImages.forEach((item) => URL.revokeObjectURL(item.preview));
    setPendingImages([]);
    return uploaded;
  };

  const handleSave = async () => {
    if (!canEdit) {
      return;
    }

    if (!executionSummary.trim()) {
      toast({
        title: "Resumo obrigatório",
        description: "Descreva o serviço realizado antes de gerar o QR Code.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const photosToUpload = pendingImages.length;
      let saved = await saveExecution({
        serviceOrderId: serviceOrder.serviceOrderid,
        executionSummary,
        executionNotes,
      });

      if (photosToUpload) {
        saved = await uploadPendingImages(serviceOrder.serviceOrderid);
      }

      setSavedExecution(saved);
      onSaved?.(saved);
      toast({
        title: "Execução registrada",
        description: photosToUpload
          ? "QR Code e fotos salvos no banco de dados."
          : "QR Code salvo no banco de dados.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao salvar execução",
        description:
          error.response?.data?.message ||
          error.message ||
          "Tente novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUploadPhotosOnly = async () => {
    if (!savedExecution || !pendingImages.length) {
      return;
    }

    setSaving(true);
    try {
      const saved = await uploadPendingImages(serviceOrder.serviceOrderid);
      setSavedExecution(saved);
      onSaved?.(saved);
      toast({
        title: "Fotos enviadas",
        description: "As imagens do serviço foram salvas.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro ao enviar fotos",
        description:
          error.response?.data?.message ||
          error.message ||
          "Tente novamente.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!publicUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(publicUrl);
      toast({
        title: "Link copiado",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Não foi possível copiar",
        description: publicUrl,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const existingImages = savedExecution?.images || [];
  const modalTitle = savedExecution
    ? canEdit
      ? "Executar OS / Fotos / QR Code"
      : "Execução da OS"
    : "Executar OS / Fotos / QR Code";

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader bg="#C1272D" color="white" borderTopRadius="xl">
          {modalTitle}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={6}>
          {loading ? (
            <VStack py={8}>
              <Spinner color="#C1272D" />
              <Text>Carregando execução...</Text>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontWeight="semibold">
                  OS #{serviceOrder?.orderNumber || "N/A"}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {savedExecution
                    ? `Executado por: ${
                        savedExecution.executedBy?.name ||
                        savedExecution.executedBy?.login ||
                        "N/A"
                      }`
                    : `Funcionário: ${
                        executedBy?.name || executedBy?.login || "N/A"
                      }`}
                </Text>
              </Box>

              {canEdit && (
                <>
                  <FormControl isRequired>
                    <FormLabel>Resumo do serviço realizado</FormLabel>
                    <Textarea
                      placeholder="Descreva o que foi executado..."
                      value={executionSummary}
                      onChange={(e) => setExecutionSummary(e.target.value)}
                      rows={4}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Observações (opcional)</FormLabel>
                    <Textarea
                      placeholder="Materiais, pendências, etc."
                      value={executionNotes}
                      onChange={(e) => setExecutionNotes(e.target.value)}
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel display="flex" alignItems="center" gap={2}>
                      <Icon as={FiUploadCloud} />
                      Fotos do serviço realizado
                    </FormLabel>
                    <Input
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={handleSelectImages}
                      border="1px solid"
                      borderColor="#625752"
                      bg="white"
                      py={1}
                    />
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Envie fotos ou vídeos do before/after, materiais ou
                      evidências do serviço. Funciona para OS atrasada, em
                      andamento ou finalizada.
                    </Text>
                  </FormControl>
                </>
              )}

              {!canEdit && savedExecution && (
                <Box p={4} bg="gray.50" borderRadius="md">
                  <Text fontWeight="semibold" mb={2}>
                    Resumo da execução
                  </Text>
                  <Text>{savedExecution.executionSummary}</Text>
                  {savedExecution.executionNotes && (
                    <>
                      <Text fontWeight="semibold" mt={4} mb={2}>
                        Observações
                      </Text>
                      <Text>{savedExecution.executionNotes}</Text>
                    </>
                  )}
                </Box>
              )}

              {pendingImages.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" mb={3}>
                    Fotos selecionadas ({pendingImages.length})
                  </Text>
                  <SimpleGrid columns={[2, 3, 4]} spacing={3}>
                    {pendingImages.map((item, index) =>
                      item.isVideo ? (
                        <Box
                          key={`${item.preview}-${index}`}
                          as="video"
                          src={item.preview}
                          controls
                          borderRadius="md"
                          border="1px solid #DDD"
                          maxH="120px"
                          w="100%"
                        />
                      ) : (
                        <Image
                          key={`${item.preview}-${index}`}
                          src={item.preview}
                          alt={`Arquivo selecionado ${index + 1}`}
                          borderRadius="md"
                          border="1px solid #DDD"
                          objectFit="cover"
                          maxH="120px"
                        />
                      )
                    )}
                  </SimpleGrid>
                </Box>
              )}

              {existingImages.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" mb={3}>
                    Fotos já enviadas ({existingImages.length})
                  </Text>
                  <SimpleGrid columns={[2, 3, 4]} spacing={3}>
                    {existingImages.map((image) =>
                      isVideoMedia(image) ? (
                        <Box
                          key={image.imageId}
                          as="video"
                          src={getExecutionImageUrl(image.url)}
                          controls
                          borderRadius="md"
                          border="1px solid #DDD"
                          maxH="120px"
                          w="100%"
                        />
                      ) : (
                        <Image
                          key={image.imageId}
                          src={getExecutionImageUrl(image.url)}
                          alt={image.originalFileName || "Foto do serviço"}
                          borderRadius="md"
                          border="1px solid #DDD"
                          objectFit="cover"
                          maxH="120px"
                        />
                      )
                    )}
                  </SimpleGrid>
                </Box>
              )}

              {savedExecution && (
                <Box
                  textAlign="center"
                  p={4}
                  borderWidth="1px"
                  borderRadius="lg"
                  bg="gray.50"
                >
                  <Text fontWeight="semibold" mb={3}>
                    QR Code público
                  </Text>
                  <Box
                    as="a"
                    href={publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    display="inline-block"
                    p={3}
                    bg="white"
                    borderRadius="md"
                  >
                    <QRCodeSVG value={publicUrl} size={180} level="M" />
                  </Box>
                  <Text fontSize="sm" mt={3} color="gray.600">
                    Escaneie para abrir o relatório público da execução.
                  </Text>
                  <Input
                    mt={3}
                    size="sm"
                    value={publicUrl}
                    isReadOnly
                    bg="white"
                  />
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {savedExecution && (
            <>
              <Button variant="outline" mr={3} onClick={handleCopyLink}>
                Copiar link
              </Button>
            </>
          )}
          {canEdit && savedExecution && pendingImages.length > 0 && (
            <Button
              variant="outline"
              borderColor="#C1272D"
              color="#C1272D"
              mr={3}
              onClick={handleUploadPhotosOnly}
              isLoading={saving}
            >
              Enviar fotos
            </Button>
          )}
          {canEdit && (
            <Button
              bg="#C1272D"
              color="white"
              _hover={{ bg: "#a81f23" }}
              mr={3}
              onClick={handleSave}
              isLoading={saving}
            >
              {savedExecution ? "Atualizar execução" : "Registrar execução"}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
